/**
 * <quantity-selector> — quantity stepper that honours the quantity rules set in the
 * SFV admin app (minimum, maximum and step/jump).
 *
 * The rule is resolved server-side in snippets/quantity-selector.liquid and arrives on the
 * input as native `min` / `max` / `step` attributes, so the browser's own validation agrees
 * with ours. Increments are counted from `min`, which is exactly what HTML's step does when
 * a min is present: min=2 step=4 => 2, 6, 10…  — the same arithmetic the backend applies
 * when it validates the order ((quantity - min) % jump == 0).
 */
class QuantitySelector extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('[data-quantity-input]');
    if (!this.input) return;

    this.minusButton = this.querySelector('[data-quantity-minus]');
    this.plusButton = this.querySelector('[data-quantity-plus]');
    this.minusWrapper = this.querySelector('[data-button-wrapper-minus]');
    this.plusWrapper = this.querySelector('[data-button-wrapper-plus]');
    this.message = this.querySelector('[data-quantity-message]');

    this.onButtonClick = this.onButtonClick.bind(this);
    this.onChange = this.onChange.bind(this);
    this.onKeydown = this.onKeydown.bind(this);

    // Capture phase, on the element rather than the buttons: this runs before any listener
    // bound to the buttons themselves and before any delegated listener on an ancestor.
    // Stopping propagation here makes this element the only thing that reacts to +/-, so a
    // theme handler still bound to those buttons cannot apply its own step on top of ours.
    this.addEventListener('click', this.onButtonClick, true);
    this.input.addEventListener('change', this.onChange);
    this.input.addEventListener('blur', this.onChange);
    this.input.addEventListener('keydown', this.onKeydown);

    // Normalise whatever the page rendered (a cart line can predate the rule).
    this.apply(this.parse(this.input.value), { silent: true, announce: false });
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onButtonClick, true);
    if (!this.input) return;
    this.input.removeEventListener('change', this.onChange);
    this.input.removeEventListener('blur', this.onChange);
    this.input.removeEventListener('keydown', this.onKeydown);
  }

  // ---------------------------------------------------------------- rule

  get min() {
    const value = parseInt(this.input.getAttribute('min'), 10);
    return Number.isFinite(value) && value > 0 ? value : 1;
  }

  get step() {
    const value = parseInt(this.input.getAttribute('step'), 10);
    return Number.isFinite(value) && value > 0 ? value : 1;
  }

  /** null means "no maximum". */
  get max() {
    const value = parseInt(this.input.getAttribute('max'), 10);
    return Number.isFinite(value) && value > 0 ? value : null;
  }

  /** The largest quantity that is both within max and on the step grid. */
  get ceiling() {
    if (this.max === null) return null;
    const steps = Math.floor((this.max - this.min) / this.step);
    return steps < 0 ? this.min : this.min + steps * this.step;
  }

  /** Whether removing the line is allowed (cart lines can go to 0, product forms cannot). */
  get allowsZero() {
    // The snippet renders the name through a Liquid conditional, so trim defensively.
    return this.input.name.trim() === 'updates[]';
  }

  // ---------------------------------------------------------------- behaviour

  parse(raw) {
    const value = parseInt(String(raw).trim(), 10);
    return Number.isFinite(value) ? value : NaN;
  }

  onButtonClick(event) {
    const target = event.target instanceof Element ? event.target : null;
    const button = target && target.closest('[data-quantity-plus], [data-quantity-minus]');
    if (!button || !this.contains(button)) return;

    // Claim the click: nothing below or above this element gets to act on it.
    event.stopPropagation();
    if (button.disabled) return;

    const direction = button.hasAttribute('data-quantity-plus') ? 1 : -1;
    const current = this.parse(this.input.value);
    const from = Number.isFinite(current) ? current : this.min;

    if (direction < 0 && this.allowsZero && from - this.step < this.min) {
      // Stepping below the minimum in the cart means "remove this line".
      this.apply(0, { exact: true });
      return;
    }

    this.apply(from + direction * this.step, { direction });
  }

  onChange() {
    this.apply(this.parse(this.input.value));
  }

  onKeydown(event) {
    if (event.key !== 'Enter') return;
    // Normalise before the form submits, so a typed value never reaches the cart unadjusted.
    this.apply(this.parse(this.input.value));
  }

  /**
   * Writes a valid quantity back to the input, explains any adjustment, and lets the theme
   * know via the events its cart code already listens for.
   */
  apply(requested, options = {}) {
    const { silent = false, direction = 0, exact = false, announce = true } = options;
    const previous = this.parse(this.input.value);
    const next = exact ? requested : this.normalise(requested, direction);

    this.input.value = String(next);
    this.refreshButtons(next);

    if (announce && !silent && Number.isFinite(requested) && !exact && requested !== next) {
      this.explain(requested, next);
    } else if (!silent) {
      this.clearMessage();
    }

    if (next !== previous) {
      this.input.dispatchEvent(new Event('input', { bubbles: true }));
      this.input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  /** Clamps into range and snaps onto the step grid, preferring not to reduce the order. */
  normalise(value, direction) {
    const { min, step, ceiling } = this;

    if (!Number.isFinite(value) || value <= min) return min;
    if (ceiling !== null && value >= ceiling) return ceiling;

    const offset = value - min;
    if (offset % step === 0) return value;

    const down = min + Math.floor(offset / step) * step;
    const up = min + Math.ceil(offset / step) * step;

    // Stepping down should land below where you were; anything else rounds up so the
    // customer is never silently given less than they asked for.
    const preferred = direction < 0 ? down : up;
    const fallback = direction < 0 ? up : down;

    if (ceiling !== null && preferred > ceiling) return fallback;
    return preferred;
  }

  refreshButtons(value) {
    const atMin = value <= this.min && !this.allowsZero;
    const atMax = this.ceiling !== null && value >= this.ceiling;

    this.toggle(this.minusButton, this.minusWrapper, atMin);
    this.toggle(this.plusButton, this.plusWrapper, atMax);
  }

  toggle(button, wrapper, disabled) {
    if (button) button.disabled = disabled;
    if (wrapper) {
      wrapper.classList.toggle('quantity-selector__button-wrapper--disabled', disabled);
    }
  }

  // ---------------------------------------------------------------- messaging

  explain(requested, applied) {
    const { min, step, ceiling } = this;

    let reason;
    if (requested < min) {
      reason = this.text('below-min', { min });
    } else if (ceiling !== null && requested > ceiling) {
      reason = this.text('above-max', { max: ceiling });
    } else {
      reason = this.text('off-step', { step, applied });
    }

    this.setMessage(reason);
  }

  /**
   * Copy comes from the Liquid snippet so it can be translated. The numbers are already
   * baked in there; only the quantity we landed on is substituted here.
   */
  text(kind, values) {
    const attribute = { 'below-min': 'textBelowMin', 'above-max': 'textAboveMax', 'off-step': 'textOffStep' }[kind];
    const template = this.dataset[attribute];

    if (!template) {
      return {
        'below-min': `Minimum ${values.min} per order.`,
        'above-max': `Maximum ${values.max} per order.`,
        'off-step': `Sold in multiples of ${values.step} — changed to ${values.applied}.`,
      }[kind];
    }

    return template.replace('%{quantity}', values.applied);
  }

  setMessage(text) {
    if (!this.message) return;
    this.message.textContent = text;
    this.message.hidden = false;
  }

  clearMessage() {
    if (!this.message) return;
    this.message.textContent = '';
    this.message.hidden = true;
  }
}

// Guard so this file is safe to load alongside a theme that already registers the element.
if (customElements.get('quantity-selector')) {
  console.warn(
    '[quantity-selector] already registered by the theme — quantity rules are NOT being ' +
      'enforced. Remove the theme’s definition so this one can take over.'
  );
} else {
  customElements.define('quantity-selector', QuantitySelector);
}